import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";

import { useFormatter } from "../../../../utils/useFormatter";
import { useDatetime } from "../../../../utils/useDatetime";
import {
  ChevronLeft,
  ChevronRight,
  History,
  WhatsApp,
  AutoAwesome,
  Article,
} from "@mui/icons-material";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useInternationalVerify } from "../../../../utils/useInternationalVerify";
import {
  useFilteredConditions,
  useFilteredAllergies,
  useFilteredImmunizations,
  useFilteredSelectedInterventions,
} from "../RegisterArea/useFilteredMedicalData";
import luxor from "../../../../assets/luxor.png";
import { formatarDadosParaTexto } from "../../../../utils/formatadorDados";
import { toast } from "react-toastify";
import { useInterventionsContext } from "../../../../contexts/InterventionsContext";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { generateMedicalTextAndCopy } from "@/utils/medicalTextBuilder";

export function Header() {
  const { formatNameDisplay, formatGender, formatAge } = useFormatter;
  const { formatDate } = useDatetime;
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());

  const {
    setOpenSummary,
    patient,
    luxorMode,
    setLuxorMode,
    registerAreaTab,
    setCopyMedicalData,
  } = useMedicalCareContext();
  const {
    activeStep: contextActiveStep,
    handleSubmit,
    condutas,
    problems,
  } = useMedicalCareContext();

  const filteredConditions = useFilteredConditions();
  const filteredAllergies = useFilteredAllergies();
  const filteredImmunizations = useFilteredImmunizations();

  const { selectedInterventions } = useInterventionsContext();
  const textoInterventions = useFilteredSelectedInterventions();

  const handleClickSummary = () => {
    setOpenSummary(true);
  };

  const steps = ["data1", "data2"];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  type DemographicDataType = {
    [key: string]: {
      label: string;
      display: any;
    };
  };

  const auxType = {
    raceExists: patient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRRacaCorEtnia-1.0"
    ),
    genderExists: patient?.extension?.find(
      (el: any) =>
        el.url ===
        "http://www.saude.gov.br/fhir/StructureDefinition/BRIdentidadeGenero"
    ),
    educationExists: patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel"
    ),
    religionExist: patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-ReligiousAffiliation"
    ),
    maritalStatusExists: patient?.maritalStatus,
    occupationExists: patient?.extension?.find(
      (el: any) =>
        el.url === "https://terminologia.saude.gov.br/fhir/ValueSet/BROcupacao"
    ),
    familyIncomeExists: patient?.extension?.find(
      (el: any) =>
        el.url === "http://www.saude.gov.br/fhir/StructureDefinition/BRRendaFamiliar"
    ),
  };

  const auxAddress = patient?.address?.[0];
  const city = auxAddress?.city || "";
  const addressState = auxAddress?.state || "";
  const postalCode = auxAddress?.postalCode || "";
  const line = auxAddress?.line?.join(", ") || "";

  const address =
    [line, city, addressState, postalCode]
      ?.filter((el: any) => el)
      ?.join(" , ") || "-";

  const demographicData: DemographicDataType = {
    displayRace: {
      label: "Raça/Cor",
      display: auxType?.raceExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayGenderIdentity: {
      label: "Identidade de gênero",
      display:
        auxType?.genderExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayEducation: {
      label: "Escolaridade",
      display:
        auxType?.educationExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayReligion: {
      label: "Religião",
      display:
        auxType?.religionExist?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayMaritalStatus: {
      label: "Estado civil",
      display: auxType?.maritalStatusExists?.coding?.[0]?.display,
    },
    displayOccupation: {
      label: "Profissão",
      display:
        auxType?.occupationExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayFamilyIncome: {
      label: "Renda Familiar",
      display:
        auxType?.familyIncomeExists?.valueCodeableConcept?.coding?.[0]?.display,
    },
    displayAddress: {
      label: "Endereço",
      display: address || "-",
    },
  };

  const phone = patient?.telecom?.find((e: any) => e.system === "phone")?.value;

  const cpf = patient?.id?.split("-")?.[1];

  const socialName =
    patient?.name?.find((el: any) => el.use === "usual")?.text || "-";

  const data = [
    {
      label: "Nome",
      description: formatNameDisplay(patient?.name?.[0]?.text || "-"),
    },
    {
      label: "Nome social",
      description: formatNameDisplay(socialName),
    },
    {
      label: demographicData?.displayOccupation?.label,
      description: demographicData?.displayOccupation?.display || "-",
    },
    {
      label: "CPF",
      description: cpf,
    },
    {
      label: "Telefone",
      description: (
        <>
          <Stack
            sx={{ height: "20px" }}
            direction="row"
            alignItems="center"
            justifyContent="center"
            alignContent="center"
          >
            <Typography minWidth="150px">
              {useFormatter.formatPhoneNumber(phone || "")}
            </Typography>
            <Tooltip title={useFormatter.formatPhoneNumber(phone || "")}>
              <IconButton
                color="primary"
                onClick={() =>
                  window.open(
                    `https://web.whatsapp.com/send?phone=+${useInternationalVerify.verifyPhoneDDI(
                      phone
                    )}`,
                    "_blank"
                  )
                }
              >
                <WhatsApp />
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      ),
    },

    {
      label: "Sexo de nascimento",
      description: formatGender(patient?.gender) || "-",
    },
    {
      label: "Idade",
      description: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
    },
    {
      label: "Data de nascimento",
      description: patient?.birthDate
        ? formatDate(`${patient?.birthDate}`)
        : "-",
    },
  ];

  const demographicsData = [
    {
      label: demographicData?.displayEducation?.label,
      description: demographicData?.displayEducation?.display || "-",
    },
    {
      label: demographicData?.displayReligion?.label,
      description: demographicData?.displayReligion?.display || "-",
    },
    {
      label: demographicData?.displayMaritalStatus?.label,
      description: demographicData?.displayMaritalStatus?.display || "-",
    },
    {
      label: demographicData?.displayFamilyIncome?.label,
      description: demographicData?.displayFamilyIncome?.display || "-",
    },
    {
      label: demographicData?.displayGenderIdentity?.label,
      description: demographicData?.displayGenderIdentity?.display || "-",
    },
    {
      label: demographicData?.displayAddress?.label,
      description: demographicData?.displayAddress?.display || "-",
    },
  ];

  const obterInformacoesPaciente = () => {
    const educacaoExtension = patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel"
    );

    const escolaridade =
      educacaoExtension?.valueCodeableConcept?.coding?.[0]?.display || "-";

    return {
      idade: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
      sexo: formatGender(patient?.gender) || "-",
      escolaridade: escolaridade,
    };
  };

  const onSubmit = (data: any) => {
    const dataComContexto = {
      ...data,
      New_Conduta: condutas,
      problems: problems,
    };
    generateMedicalTextAndCopy({
      data: dataComContexto,
      infosPaciente: obterInformacoesPaciente(),
      filteredConditions,
      filteredAllergies,
      filteredImmunizations,
      textoInterventions,
      setContent: (textoFinal) => setCopyMedicalData(textoFinal),
      onSuccess: () =>
        toast.success("Dados de atendimento copiados com sucesso!"),
      onError: () => toast.error("Erro ao copiar dados!"),
    });
  };

  return (
    <>
      <Box pl={6} sx={{ direction: "row" }}>
        <Stack direction="row" alignItems="center" spacing={4}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={4}
            justifyContent="center"
            width="100%"
            gap={2}
          >
            <Tooltip title="Anterior">
              <IconButton onClick={handleBack} disabled={activeStep === 0}>
                <ChevronLeft
                  sx={{
                    color:
                      activeStep === 0 ? "neutral700.main" : "primary800.main",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              width="100%"
              padding={2}
              gap={2}
            >
              {activeStep === 0 && (
                <Stack
                  direction="row"
                  sx={{ width: "100%" }}
                  gap={4}
                  justifyContent={"space-between"}
                >
                  {data?.map((el) => (
                    <Stack direction="column">
                      <Stack direction="row">
                        <Typography
                          variant="body1"
                          color="neutral1000.main"
                          fontWeight={500}
                        >
                          {el.label}
                        </Typography>
                      </Stack>
                      <Typography variant="body1" color="neutral700.main">
                        {el.description}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              {activeStep === 1 && (
                <Stack direction="row" sx={{ width: "100%" }} gap={4}>
                  {demographicsData?.map((el) => (
                    <Stack
                      direction="column"
                      key={`patient-information-${el.label}`}
                    >
                      <Stack direction="row">
                        <Typography
                          variant="body1"
                          color="neutral1000.main"
                          fontWeight={500}
                        >
                          {el.label}
                        </Typography>
                      </Stack>
                      <Typography variant="body1" color="neutral700.main">
                        {el.description}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>

            <Tooltip title="Próximo">
              <IconButton
                onClick={handleNext}
                disabled={activeStep === steps.length - 1}
              >
                <ChevronRight
                  sx={{
                    color:
                      activeStep === steps.length - 1
                        ? "neutral700.main"
                        : "primary800.main",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>

          {contextActiveStep !== 2 && (
            <Tooltip
              title={
                luxorMode
                  ? contextActiveStep === 1
                    ? "Ver Intervenções"
                    : "Ver histórico"
                  : "Analisar atendimento com IA"
              }
            >
              <IconButton
                sx={{
                  width: 36,
                  height: 36,
                  border: "1px solid",
                  borderColor: luxorMode ? "primary.main" : "secondary.main",
                  bgcolor: luxorMode ? "primary.light" : "transparent",
                }}
                onClick={() => {
                  if (!luxorMode) {
                    handleSubmit(onSubmit)();
                  }
                  setLuxorMode(!luxorMode);
                }}
              >
                {luxorMode ? (
                  <Article />
                ) : (
                  <Avatar
                    src={luxor}
                    alt="Luxor"
                    sx={{ width: 36, height: 36 }}
                  />
                )}
              </IconButton>
            </Tooltip>
          )}

          <Button
            sx={{
              width: "200px",
              textTransform: "none",
            }}
            startIcon={<History />}
            onClick={handleClickSummary}
          >
            Sumário de saúde
          </Button>
        </Stack>
      </Box>
    </>
  );
}
